import { useState } from 'react';
import { ArrowUp, ArrowDown, ImageOff, ImagePlus, RotateCcw } from 'lucide-react';
import { getAllCategories } from '../../../data/catalog';
import { useSiteConfig, saveSiteConfig, getHomeCategories, fileToDataUrl } from '../../../utils/siteConfig';
import { useQueue } from '../../../context/QueueContext';

export default function CategoriesSection({ flash }) {
  const { user } = useQueue();
  const cfg = useSiteConfig();
  const [list, setList] = useState(() => getHomeCategories(cfg));

  const move = (i, dir) => {
    const next = [...list];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    setList(next);
  };

  const patch = (i, p) => setList((l) => l.map((x, xi) => (xi === i ? { ...x, ...p } : x)));

  const upload = async (e, i) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const dataUrl = await fileToDataUrl(file, 600);
    patch(i, { image: dataUrl, customImage: true });
    e.target.value = '';
  };

  const save = async () => {
    const payload = list.map((item) => {
      const cat = getAllCategories().find((c) => c.slug === item.slug);
      return {
        slug: item.slug,
        show: item.show,
        image: item.customImage ? item.image : null,
        tagline: item.tagline !== cat?.tagline ? item.tagline : null,
      };
    });
    try {
      await saveSiteConfig({ categories: payload }, user, 'Alterou as categorias em destaque da Home.');
      flash('Categorias em destaque salvas.');
    } catch (e) {
      flash(e.message || 'Falha ao salvar as categorias em destaque.');
    }
  };

  return (
    <section className="rounded-2xl border border-navy/10 bg-white p-5" data-testid="pers-categories-section">
      <h3 className="font-serif text-xl text-navy">Categorias em Destaque</h3>
      <p className="mt-1 text-sm text-muted-foreground">
        Controla quais categorias reais aparecem na Home, a ordem, a imagem de destaque e o pequeno texto. O cadastro da categoria não é duplicado.
        Categorias cadastradas pelo painel entram aqui como ocultas — ative para exibir na Home.
      </p>

      <div className="mt-4 space-y-3">
        {list.map((item, i) => (
          <div key={item.slug} className={`rounded-2xl border p-3 ${item.show ? 'border-navy/10 bg-white' : 'border-navy/10 bg-navy-light/30 opacity-70'}`} data-testid={`pers-cat-row-${item.slug}`}>
            <div className="flex items-center gap-3">
              {item.image ? (
                <img src={item.image} alt={item.name} className="h-16 w-12 shrink-0 rounded-lg object-cover" />
              ) : (
                <div className="flex h-16 w-12 shrink-0 items-center justify-center rounded-lg bg-navy-light" data-testid={`pers-cat-noimage-${item.slug}`}>
                  <ImageOff className="h-4 w-4 text-navy/40" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="font-medium leading-tight">{item.name}</p>
                <input
                  value={item.tagline}
                  onChange={(e) => patch(i, { tagline: e.target.value })}
                  aria-label={`Texto de apresentação de ${item.name}`}
                  data-testid={`pers-cat-tagline-${item.slug}`}
                  className="mt-1 h-9 w-full rounded-lg border border-input bg-white px-3 text-xs outline-none focus:border-navy"
                />
              </div>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <button type="button" onClick={() => move(i, -1)} disabled={i === 0} aria-label="Mover para cima" data-testid={`pers-cat-up-${item.slug}`} className="flex h-10 w-10 items-center justify-center rounded-xl border border-navy/20 text-navy hover:bg-navy-light disabled:opacity-30">
                <ArrowUp className="h-4 w-4" />
              </button>
              <button type="button" onClick={() => move(i, 1)} disabled={i === list.length - 1} aria-label="Mover para baixo" data-testid={`pers-cat-down-${item.slug}`} className="flex h-10 w-10 items-center justify-center rounded-xl border border-navy/20 text-navy hover:bg-navy-light disabled:opacity-30">
                <ArrowDown className="h-4 w-4" />
              </button>
              <button type="button" onClick={() => patch(i, { show: !item.show })} aria-pressed={item.show} data-testid={`pers-cat-toggle-${item.slug}`} className={`flex h-10 items-center rounded-xl border px-4 text-xs font-semibold ${item.show ? 'border-emerald-500/50 text-emerald-700 hover:bg-emerald-50' : 'border-amber-400/60 text-amber-700 hover:bg-amber-50'}`}>
                {item.show ? 'VISÍVEL NA HOME' : 'OCULTA NA HOME'}
              </button>
              <label htmlFor={`pers-cat-img-${item.slug}`} data-testid={`pers-cat-image-${item.slug}`} className="flex h-10 cursor-pointer items-center gap-2 rounded-xl border border-navy/20 px-4 text-xs font-semibold text-navy hover:bg-navy-light">
                <ImagePlus className="h-4 w-4" /> IMAGEM
              </label>
              <input id={`pers-cat-img-${item.slug}`} type="file" accept="image/*" className="sr-only" onChange={(e) => upload(e, i)} />
              {item.customImage && (
                <button type="button" onClick={() => { const cat = getAllCategories().find((c) => c.slug === item.slug); patch(i, { image: cat?.image || null, customImage: false }); }} data-testid={`pers-cat-image-reset-${item.slug}`} className="flex h-10 items-center gap-2 rounded-xl border border-red-300 px-4 text-xs font-semibold text-red-700 hover:bg-red-50">
                  <RotateCcw className="h-4 w-4" /> PADRÃO
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <button type="button" onClick={save} data-testid="pers-categories-save" className="mt-4 w-full rounded-full bg-navy px-6 py-3.5 text-sm font-semibold text-white hover:bg-navy-hover sm:w-auto">
        SALVAR ALTERAÇÕES
      </button>
    </section>
  );
}
